import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, Linking, RefreshControl } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { useNavigation } from '@react-navigation/native';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import type { StackNavigationProp } from '@react-navigation/stack';
import { DesktopHeaderCard } from '@/components/residents/desktop/DesktopHeaderCard';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MaintenanceCard } from './MaintenanceCard';
import { TicketTypes } from './MaintenanceEmptyState';
import { MaintenanceList } from './MaintenanceList';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { useAppSelector } from '@/store';

interface MaintenanceOpenProps {
  onCreateTicket?: () => void;
}

export const MaintenanceOpen: React.FC<MaintenanceOpenProps> = ({ onCreateTicket }) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const { openTickets, loading, error, refresh } = useMaintenanceTickets();
  const user = useAppSelector((s) => s.auth.user as any);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

  const maintenancePhone: string | undefined = useMemo(() => {
    return user?.property?.phone || user?.property?.twilioNumber || undefined;
  }, [user]);

  const maintenanceHours = user?.property?.maintenanceHours as any;

  if (!isDesktop) {
    // Mobile view
    return (
      <ScrollView
        style={styles.mobileContainer}
        contentContainerStyle={styles.mobileContent}
        refreshControl={
          <RefreshControl refreshing={!!loading} onRefresh={refresh} tintColor="#007AFF" />
        }
      >
        <View style={styles.mobileHeader}>
          <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={40} height={40} />
          <View style={styles.mobileHeaderText}>
            <Text style={styles.mobileTitle}>Need something fixed?</Text>
            <Text style={styles.mobileSubtitle}>
              Submit a new request with a brief description of the problem, and we'll take care of the rest.
            </Text>
          </View>
        </View>

        {!!maintenancePhone && (
          <View style={styles.phoneRow}>
            <Text style={styles.phoneTitle}>For emergencies, call</Text>
            <TouchableOpacity
              onPress={() => {
                if (Platform.OS === 'ios') {
                  const { ActionSheetIOS } = require('react-native');
                  ActionSheetIOS.showActionSheetWithOptions(
                    { options: ['Call', 'Text', 'Cancel'], cancelButtonIndex: 2 },
                    (idx: number) => {
                      if (idx === 0) Linking.openURL(`tel:${maintenancePhone}`);
                      else if (idx === 1) Linking.openURL(`sms:${maintenancePhone}`);
                    }
                  );
                } else {
                  Linking.openURL(`tel:${maintenancePhone}`);
                }
              }}
            >
              <Text style={styles.phoneNumber}>{maintenancePhone}</Text>
            </TouchableOpacity>
          </View>
        )}

        {!!maintenanceHours && (
          <View style={styles.scheduleCard}>
            <Text style={styles.scheduleTitle}>Maintenance hours</Text>
            {Object.keys(maintenanceHours).map((dayKey) => {
              const hours = maintenanceHours[dayKey];
              return (
                <View key={dayKey} style={styles.scheduleRow}>
                  <Text style={styles.scheduleDay}>{dayKey}</Text>
                  <Text style={styles.scheduleTime}>{hours?.start || '-'} - {hours?.end || '-'}</Text>
                </View>
              );
            })}
          </View>
        )}
        
        <RNButton
          title="Request a service"
          onPress={() => {
            if (onCreateTicket) return onCreateTicket();
            navigation.navigate('CreateMaintenanceTicket');
          }}
          variant="tertiary"
          style={styles.mobileButton}
        />

        <MaintenanceList
          loading={loading}
          error={error}
          items={openTickets}
          emptyType={TicketTypes.OPEN}
          renderItem={(t) => (<MaintenanceCard key={t.maintenanceRequestId} ticket={t} />)}
        />
      </ScrollView>
    );
  }

  // Desktop view
  return (
    <View style={styles.desktopContainer}>
      <DesktopHeaderCard
        icon={<RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={64} height={64} />}
        title="Need something fixed?"
        subtitleLines={[
          "Submit a new request with a brief description of the problem, and we'll take care of the rest."
        ]}
        ctaText="Request a service"
        onPressCta={onCreateTicket}
        style={styles.headerCard}
      />

      <MaintenanceList
        loading={loading}
        error={error}
        items={openTickets}
        emptyType={TicketTypes.OPEN}
        renderItem={(t) => (<MaintenanceCard key={t.maintenanceRequestId} ticket={t} />)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileContainer: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  mobileContent: {
    padding: 16,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  mobileHeaderText: {
    flex: 1,
    marginLeft: 12,
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  mobileSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  phoneTitle: {
    fontSize: 13,
    color: '#6B7280',
  },
  phoneNumber: {
    fontSize: 15,
    color: '#007AFF',
    fontWeight: '600',
  },
  scheduleCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  scheduleTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  scheduleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  scheduleDay: {
    fontSize: 14,
    color: '#374151',
  },
  scheduleTime: {
    fontSize: 14,
    color: '#6B7280',
  },
  mobileButton: {
    marginBottom: 24,
  },
  ticketsContainer: {
    gap: 12,
  },

  // Desktop styles
  desktopContainer: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    padding: 24,
    width: '100%',
  },
  headerCard: {
    marginBottom: 24,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    marginTop: 16,
    paddingHorizontal: 32,
  },
  desktopLoadingText: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    marginTop: 16,
    paddingHorizontal: 32,
  },
  desktopTicketsGrid: {
    width: '100%',
  },
  desktopGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    ...(Platform.OS === 'web' && ({
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)', // 3 columns like resio-web
      gridGap: 16,
    } as any)),
  },
});
